import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { setTokens } from '../lib/auth'

// ─────────────────── Types ───────────────────
type InviteInfo = {
  email: string
  role_name: string
  role_description: string
  invited_by: string | null
  message: string
  expires_at: string
  status: 'PENDING'
}

type AcceptResponse = {
  access: string
  refresh: string
  user: { id: number; username: string; email: string; first_name: string; last_name: string }
  person_id: number
  role: string
}

// ─────────────────── Page ───────────────────
// Public page — no auth required. Reachable at /invite/accept/<token>.
// The token comes from the URL, so we can preload context (role, inviter,
// expiry) and let the user finalise signup with just password + name.
export default function InviteAccept() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  // Probe the invite up front — bad/expired tokens get a clean error
  // page rather than crashing on submit.
  const infoQ = useQuery({
    queryKey: ['invite-info', token],
    queryFn: async () => {
      const resp = await axios.get<InviteInfo>(`/api/v1/invite/info/?token=${token}`)
      return resp.data
    },
    enabled: !!token,
    retry: false,
  })

  // If the recipient is already logged in elsewhere, the JWT we get back
  // here replaces theirs — fine, but bail early to avoid a confusing
  // "you're already logged in" feeling. (Keep it simple: just warn.)
  useEffect(() => {
    if (localStorage.getItem('mooderp.access')) {
      // Not blocking — just informational.
      // eslint-disable-next-line no-console
      console.log('Existing session will be replaced once the invite is accepted.')
    }
  }, [])

  const acceptMut = useMutation({
    mutationFn: async () => {
      if (password !== confirm) throw new Error('Passwords do not match')
      if (password.length < 8) throw new Error('Password must be at least 8 characters')
      const resp = await axios.post<AcceptResponse>('/api/v1/invite/accept/', {
        token,
        password,
        first_name: firstName,
        last_name: lastName,
      })
      return resp.data
    },
    onSuccess: (data) => {
      setTokens(data.access, data.refresh)
      toast.success(`Welcome, ${data.user.first_name || data.user.username}!`)
      navigate('/admin')
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail
        || e?.response?.data?.password
        || e?.message
        || 'Something went wrong'
      toast.error(detail)
    },
  })

  if (infoQ.isLoading) {
    return (
      <Centered>
        <p className="text-muted-foreground">Loading invite…</p>
      </Centered>
    )
  }

  if (infoQ.isError || !infoQ.data) {
    const status = (infoQ.error as any)?.response?.status
    const reason = (infoQ.error as any)?.response?.data?.detail || 'Invite not found.'
    return (
      <Centered>
        <div className="bg-card rounded-lg shadow p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-foreground mb-2">
            {status === 410 ? 'Invite no longer valid' : 'Invite not found'}
          </h1>
          <p className="text-sm text-muted-foreground mb-4">{reason}</p>
          <p className="text-xs text-muted-foreground">
            Ask whoever invited you to send a fresh link.
          </p>
          <div className="mt-4">
            <Link to="/login" className="text-violet-600 hover:underline text-sm">
              Already have an account? Sign in →
            </Link>
          </div>
        </div>
      </Centered>
    )
  }

  const info = infoQ.data
  const expires = new Date(info.expires_at)
  const daysLeft = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / 86_400_000))

  return (
    <Centered>
      <div className="bg-card rounded-lg shadow p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-foreground mb-1">
          Welcome to MooderERP
        </h1>
        <p className="text-sm text-muted-foreground">
          {info.invited_by ? <><strong>{info.invited_by}</strong> invited you </> : 'You\'ve been invited '}
          to join as <strong>{info.role_name}</strong>.
        </p>
        {info.role_description && (
          <p className="text-xs text-muted-foreground mt-1">{info.role_description}</p>
        )}
        {info.message && (
          <blockquote className="mt-3 border-l-2 border-violet-300 pl-3 text-sm italic text-muted-foreground">
            “{info.message}”
          </blockquote>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          Signing up as <strong>{info.email}</strong>. Invite expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}.
        </p>

        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => { e.preventDefault(); acceptMut.mutate() }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="acc-first" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                First name
              </label>
              <Input id="acc-first" value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Alex" />
            </div>
            <div>
              <label htmlFor="acc-last" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Last name
              </label>
              <Input id="acc-last" value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe" />
            </div>
          </div>

          <div>
            <label htmlFor="acc-pw" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Set a password *
            </label>
            <Input
              id="acc-pw" type="password" required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="acc-pw2" className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Confirm password *
            </label>
            <Input
              id="acc-pw2" type="password" required minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter"
              autoComplete="new-password"
            />
          </div>

          <Button type="submit" className="w-full" disabled={acceptMut.isPending}>
            {acceptMut.isPending ? 'Creating account…' : 'Accept invite & sign in'}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            By accepting, you create an account at <strong>{window.location.host}</strong>.
          </p>
        </form>
      </div>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      {children}
    </div>
  )
}
